import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import Tooltip from './Tooltip';
import { addLeaseChangeListener, removeLeaseChangeListener, dispatchLeaseChangeEvent } from '../leaseEventSystem';
import EditLeaseModal from './EditLeaseModal';
import { showConfirm, showAlert } from '../utils/ModalService';


interface Lease {
  id: string;
  custom_id?: string;
  tenant_name: string;
  start_date: string;
  end_date: string;
  rent_amount: number;
  document_id?: string; // Add document_id here
}

interface PropertyLeasesProps {
  propertyId: string;
}

const PropertyLeases: React.FC<PropertyLeasesProps> = ({ propertyId }) => {
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [editingLease, setEditingLease] = useState<Lease | null>(null);

  const fetchLeases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/leases?property_id=${propertyId}`);
      setLeases(response.data);
    } catch (err: any) {
      console.error('Error fetching leases:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Error fetching leases.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchLeases();
    }
    addLeaseChangeListener(fetchLeases);
    return () => {
      removeLeaseChangeListener(fetchLeases);
    };
  }, [propertyId]);

  const handleViewLease = async (leaseId: string) => {
    setMessage('Opening document...');
    try {
      const response = await api.get(`/leases/${leaseId}/view`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] as string }));
      window.open(url, '_blank');
      setMessage('');
    } catch (error: any) {
      console.error('Error viewing document:', error.response?.data || error.message);
      setMessage('Could not open document. Please ensure you are logged in.');
    }
  };

  const handleDeleteLease = async (leaseId: string) => {
    setMessage('');
    showConfirm('Confirm Action', 'Are you sure you want to delete this lease?', async () => {
      try {
        await api.delete(`/leases/${leaseId}`);
        setMessage('Lease deleted successfully!');
        dispatchLeaseChangeEvent(); // Refresh list
      } catch (error: any) {
        console.error('Error deleting lease:', error.response?.data || error.message);
        setMessage(error.response?.data?.detail || 'An error occurred during lease deletion.');
      }
    });
  };

  const handleEditLease = (lease: Lease) => {
    setEditingLease(lease);
  };

  const handleCloseModal = () => {
    setEditingLease(null);
  };

  if (loading) {
    return <div>{t("propertyLeases.k1")}</div>;
  }

  if (error) {
    return <div className="text-danger">{t("propertyLeases.k2")} {error}</div>;
  }

  return (
    <div className="bg-bg-secondary p-6 rounded-lg shadow border border-border-primary mb-8">
      <EditLeaseModal lease={editingLease} onClose={handleCloseModal} />
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-primary">{t("propertyLeases.k3")}</h2>
        <Tooltip text="All leases associated with this property are listed here." />
      </div>
      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leases.length === 0 ? (
          <p className="text-text-secondary">{t("propertyLeases.k4")}</p>
        ) : (
          leases.map((lease) => {
            return (
            <div key={lease.id} className="bg-bg-tertiary p-4 rounded shadow border border-border-secondary">
              <h4 className="text-lg font-semibold mb-2 text-text-primary">{t("propertyLeases.k5")} {lease.custom_id || lease.id.substring(0, 8)}...</h4>
              {lease.custom_id && <p className="text-sm text-text-secondary mb-1">{t("propertyLeases.k6")} {lease.id.substring(0, 8)}...</p>}
              <p className="text-sm text-text-secondary mb-1"><strong>{t("propertyLeases.k7")}</strong> {lease.tenant_name || 'N/A'}</p>
              <p className="text-text-secondary"><strong>{t("propertyLeases.k8")}</strong> {lease.start_date ? new Date(lease.start_date).toLocaleDateString('en-US') : 'N/A'}</p>
              <p className="text-text-secondary"><strong>{t("propertyLeases.k9")}</strong> {lease.end_date ? new Date(lease.end_date).toLocaleDateString('en-US') : 'N/A'}</p>
              <p className="text-text-secondary"><strong>{t("propertyLeases.k10")}</strong> {typeof lease.rent_amount === 'number' ? lease.rent_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {lease.document_id && (
                  <button onClick={() => handleViewLease(lease.id)} className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 text-sm">{t("propertyLeases.k11")}</button>
                )}
                <button onClick={() => handleEditLease(lease)} className="bg-success text-white px-3 py-1 rounded hover:bg-success-dark text-sm">{t("propertyLeases.k12")}</button>
                <button onClick={() => handleDeleteLease(lease.id)} className="bg-danger text-white px-3 py-1 rounded hover:bg-red-700 text-sm">{t("propertyLeases.k13")}</button>
              </div>
            </div>
          )})
        )}
      </div>
    </div>
  );
};

export default PropertyLeases;
